import { useCallback, useSyncExternalStore } from 'react';

export type SidebarMode = 'full' | 'rail';
const STORAGE_KEY = 'fhq-sidebar-mode';
const DEFAULT_MODE: SidebarMode = 'full';

let cachedRaw: string | null | undefined;
let cachedMode: SidebarMode = DEFAULT_MODE;

function readStore(): SidebarMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw) return cachedMode;
    cachedRaw = raw;
    cachedMode = raw === 'rail' || raw === 'full' ? raw : DEFAULT_MODE;
    return cachedMode;
  } catch {
    return cachedMode;
  }
}

function writeStore(mode: SidebarMode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // private mode / SSR — fall through, cache still reflects intent
  }
  cachedRaw = mode;
  cachedMode = mode;
  window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
}

function subscribe(callback: () => void) {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) callback();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

export function useSidebarMode() {
  const mode = useSyncExternalStore(
    subscribe,
    readStore,
    () => DEFAULT_MODE,
  );

  const setMode = useCallback((next: SidebarMode) => writeStore(next), []);
  const toggle = useCallback(() => writeStore(readStore() === 'full' ? 'rail' : 'full'), []);

  return { mode, setMode, toggle };
}
